import type { ChinaPaymentRecordStatus } from './china-payment-records-wire.js';
import type { CouponStatus } from './coupons.js';
import type { OrderStatus } from './orders.js';
import type { PackageStatus } from './packages.js';
import type { TopUpStatus } from './top-up.js';

/**
 * How a status chip is coloured (MYP-22). One of five, and nothing else:
 *
 * - `muted` — the ordinary case: nothing to do, nothing wrong.
 * - `action` — waiting on the customer (brand dot, ink label). Exactly the order states that carry
 *   a primary button (see `isActionRequired`).
 * - `brand` — usable right now (a valid coupon): brand dot and brand label.
 * - `success` — finished well (a package shipped, a top-up credited).
 * - `danger` — something went wrong (an order or package exception, a failed top-up).
 */
export type ChipTone = 'muted' | 'action' | 'brand' | 'success' | 'danger';

/**
 * The one map from every status the app shows to its chip colour (MYP-22).
 *
 * Keyed by the app's own status groups, not the vendor's raw codes: the 18 order codes and 22
 * package codes are grouped by `toOrderStatus` / the packages normaliser first, and the chip shows
 * the vendor's own label for the exact code. `Record` over each union means a new status cannot
 * be added anywhere without the compiler asking for its colour here.
 */
export const STATUS_TONES = {
  order: {
    collecting: 'muted',
    unpaid: 'action',
    preparingDespatch: 'muted',
    shipped: 'action',
    received: 'muted',
    cancelled: 'muted',
    exception: 'danger',
  } satisfies Record<OrderStatus, ChipTone>,
  package: {
    on_the_way: 'muted',
    arrived: 'muted',
    in_warehouse: 'muted',
    in_order: 'muted',
    shipped: 'success',
    exception: 'danger',
  } satisfies Record<PackageStatus, ChipTone>,
  coupon: {
    valid: 'brand',
    scheduled: 'muted',
    used: 'muted',
    expired: 'muted',
  } satisfies Record<CouponStatus, ChipTone>,
  topUp: {
    success: 'success',
    failed: 'danger',
    pending: 'muted',
    // Started and never paid (`INVALID`, MYP-107): nothing went wrong, nothing happened.
    incomplete: 'muted',
    // The customer cancelled (`CANCEL`, MYP-134): their choice, not an error.
    cancelled: 'muted',
  } satisfies Record<TopUpStatus, ChipTone>,
  chinaPayment: {
    // Waiting on staff, not on the customer (MYP-63).
    review: 'muted',
    // Refused by staff, with a reason worth reading — red, as on the old website.
    cancelled: 'danger',
    // A code not seen yet: shown with the vendor's label, coloured as nothing in particular.
    other: 'muted',
  } satisfies Record<ChinaPaymentRecordStatus, ChipTone>,
} as const;
